import {
  type KeyboardEvent,
  memo,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import styles from "./ChatTab.module.css";
import { InputRequestPrompt } from "./InputRequestPrompt";
import { type SideThreadAnchor, SideThreadPanel } from "./SideThreadPanel";
import { SideThreadsList } from "./SideThreadsList";
import { SlashCommandMenu } from "./SlashCommandMenu";
import {
  commandFragment,
  completeCommand,
  matchCommands,
} from "./slashCommands";
import { speakerName } from "./speakerName";
import { Avatar } from "../../components/Avatar";
import { Button } from "../../components/Button";
import {
  AttachmentChips,
  formatBytes,
  useComposerAttachments,
} from "../../components/composerAttachments";
import { Markdown } from "../../components/Markdown";
import { MessageAttachments } from "../../components/MessageAttachments";
import { ThreadMedia } from "../../components/ThreadMedia";
import {
  TOOL_STATUS_COLOR,
  ToolCallDetail,
  ToolCallLine,
  toolDuration,
  toolLabel,
  ToolName,
} from "../../components/ToolCallLine";
import {
  type ApiAgentCommand,
  retryAgentTurn,
  sendChatMessage,
  toolCallImageFilename,
  toolCallImagePath,
  useAgentCommands,
  useChatMessages,
  useTaskInputRequest,
} from "../../data/api/chat";
import { ApiError } from "../../data/api/client";
import { type ApiSideThread, useSideThreads } from "../../data/api/sideThreads";
import { interruptTask } from "../../data/api/tasks";
import type { ApiTaskStatus, ApiToolCallUpdate } from "../../data/api/types";
import { useRealtimeScope } from "../../data/realtime/useRealtimeScope";
import type { Message, SystemStep, ToolCall } from "../../data/types";
import { Icon } from "../../icons";
import { APP_NAME } from "../../lib/appName";
import {
  chatDraftAttachmentsKey,
  chatDraftTextKey,
  usePersistedDraftText,
} from "../../lib/chatDrafts";
import { cx } from "../../lib/cx";
import { isSideThreadUnread } from "../../lib/sideThreadReads";
import { useAutosizeTextarea } from "../../lib/useAutosizeTextarea";
import { paths } from "../../routing/paths";

export type ChatTabProps = {
  readonly canSend: boolean;
  /**
   * The task's repository, which a task message's sender is linked under — a
   * family never leaves one. Without it the sender is named, not linked.
   */
  readonly repoId?: string;
  readonly taskId: string;
  readonly taskStatus: ApiTaskStatus;
  /**
   * The task's workspace, which an upload is scoped to (`api.md` §Chat
   * attachments). Without it the attach affordances are simply absent.
   */
  readonly workspaceId?: string;
};

// The daemon emits a row for each piece of a turn (text, tool use, thinking),
// but those rows still have one visual author. Collapse adjacent agent rows
// into a single author group; a user message or system event starts a new one.
const groupMessages = (
  messages: ReadonlyArray<Message>,
): ReadonlyArray<ReadonlyArray<Message>> => {
  const groups: Message[][] = [];
  for (const message of messages) {
    const previous = groups.at(-1);
    if (message.role === "agent" && previous?.[0]?.role === "agent") {
      previous.push(message);
    } else {
      groups.push([message]);
    }
  }
  return groups;
};

// What an author group actually renders: a message's own body (text, a
// streaming bubble, the thinking dots, the files it carries) or a block of
// tool calls.
//
// Tool calls are pulled out of their rows because a run of them is not a
// per-row fact: the daemon emits one row per `tool_use`, so five reads in a row
// are five rows, and the collapsing below has to see them as one sequence. A
// row with nothing but tool calls contributes no message part of its own — it
// has no body to show (chat.md §Tool use).
type GroupPart =
  | {
      readonly key: string;
      readonly kind: "message";
      readonly message: Message;
    }
  | {
      readonly key: string;
      readonly kind: "tools";
      readonly tools: ReadonlyArray<ToolCall>;
    };

const groupParts = (
  messages: ReadonlyArray<Message>,
): ReadonlyArray<GroupPart> => {
  const parts: GroupPart[] = [];
  // The block still being filled. Its part is pushed as soon as the first call
  // opens it, and the array keeps growing while the rows that follow carry
  // nothing but tool calls.
  let open: ToolCall[] | null = null;
  for (const message of messages) {
    // Files count as a body. `attach_files` publishes with no text at all
    // (chat.md §Attachments), so requiring one drops the whole message.
    if (
      message.thinking ||
      message.text ||
      message.streaming ||
      message.attachments?.length
    ) {
      // A body of its own ends the block above it: prose is a new thought.
      open = null;
      parts.push({ key: message.id, kind: "message", message });
    }
    for (const tool of message.tools ?? []) {
      if (open === null) {
        open = [tool];
        parts.push({ key: `${message.id}:tools`, kind: "tools", tools: open });
      } else {
        open.push(tool);
      }
    }
  }
  return parts;
};

// Is a narrative system card still being written by the server?
//
// The provisioning card is the one row in the thread that changes in place
// (realtime.md §Messages that change): the create route inserts it empty and
// every stage rewrites it, until the sandbox is up or the provision has
// failed. A step still open, or a task that has not started yet, both mean
// more rewrites are coming; so does a card that is still the empty placeholder,
// which is what a card none of whose updates have landed looks like.
//
// A failure ends it. Provisioning stopped on that step, so nothing further is
// written and the card is as finished as a successful one.
//
// Shared by the renderer — which holds the card open while it is being written
// — and by the realtime handler, which re-reads it until it settles. Both need
// "still moving" to mean the same thing.
const cardInFlight = (
  steps: ReadonlyArray<SystemStep>,
  taskStatus: ApiTaskStatus | undefined,
): boolean =>
  !steps.some((s) => !s.ok && !s.pending) &&
  (steps.length === 0 ||
    steps.some((s) => s.pending) ||
    taskStatus === "pending");

// The composer placeholder reflects the agent's current state, not a static
// string (chat.md §Composer) — idle vs starting-up vs working vs needs-input.
//
// Whether the task is closed is `canSend`'s call, not the status's: an
// unarchived `done` task still takes follow-ups (TaskView §canSend), so
// keying the closed copy off `status === "done"` would have contradicted a
// composer the user can type in.
const composerPlaceholder = (
  status: ApiTaskStatus,
  canSend: boolean,
): string => {
  if (!canSend) {
    return "This task is closed to new messages.";
  }
  switch (status) {
    case "needs_input":
      return "Tinsmith needs your input — reply above.";
    case "pending":
      return "Tinsmith is starting up…";
    case "running":
      return "Tinsmith is working… (↵ to send, ⇧↵ for new line)";
    default:
      return "Reply to Tinsmith… (↵ to send, ⇧↵ for new line)";
  }
};

// The chat kinds — the only events this pane re-reads for. Not all of them
// move the transcript: `chat.input_request.*` move the needs-input prompt
// beside it, and `resync` is a control frame saying events may have been
// missed. What they share is that this pane owns the answer.
//
// Deliberately an allowlist, and named as one in `realtime.md` §Caret rules:
// the task scope carries every other surface's events too, so a kind added to
// §Event kinds later is inert here until somebody names it.
//
// `chat.tool_call.updated` and `chat.message.streaming` are absent because
// they are handled above and never refetch — both carry what they change.
const CHAT_KINDS: ReadonlySet<string> = new Set([
  "chat.input_request.answered",
  "chat.input_request.posted",
  "chat.message.appended",
  "chat.message.updated",
  "resync",
]);

// How near the bottom still counts as following along (chat.md §Auto-follow).
const FOLLOW_SLACK_PX = 120;

// How near the top pulls the previous page. A distance from the *other* end,
// and unrelated to the slack above however alike the two numbers look — the
// sibling log pane keeps them at 24 and 120 (`ServicesTab`), and one constant
// standing for both would make a change to either silently move the other.
const PAGE_BACK_PX = 120;

// A `chat.tool_call.updated` summary, or null if the frame is not one the pane
// can apply. The card is rendered straight from these fields, so a summary
// missing the ids that place it is dropped rather than merged into a row it
// cannot name (`realtime.md` §Tool calls).
const toolCallUpdate = (summary: unknown): ApiToolCallUpdate | null => {
  const fields = (summary ?? {}) as Partial<ApiToolCallUpdate>;
  return typeof fields.public_id === "string" &&
    typeof fields.message_public_id === "string" &&
    typeof fields.message_sequence === "number"
    ? (fields as ApiToolCallUpdate)
    : null;
};

// The side drawer's open state. "list" is the chronological overview of
// every thread on the task. "new" is a draft anchored on a primary message
// that has no thread yet — the panel exists to collect its first message.
// "existing" reopens one already in the list. The two thread states carry
// the anchor themselves rather than just an id, because the two entry points
// that reach them (side-threads.md §UX) carry different data: a loaded
// `Message` for a fork, a `GET /threads` row's `{public_id, body}` for the
// list.
type SidePanelState =
  | {
      readonly anchor: SideThreadAnchor;
      readonly kind: "existing";
      readonly threadId: string;
    }
  | { readonly anchor: SideThreadAnchor; readonly kind: "new" }
  | { readonly kind: "list" };

// How many primary-thread rows the panel's "leading up to this" disclosure
// offers, at most — enough to place the question, not a second transcript.
const CONTEXT_WINDOW = 6;

// The primary-thread messages just before the anchor, oldest first — what
// the panel's "leading up to this" disclosure shows (side-threads.md §UX).
// Empty when the anchor isn't in the currently-loaded primary window (a
// thread reopened after the primary thread paged its early history out),
// which the panel treats as "nothing to show" rather than guessing.
const anchorContext = (
  anchorPublicId: string,
  messages: ReadonlyArray<Message>,
): ReadonlyArray<Message> => {
  const idx = messages.findIndex((m) => m.id === anchorPublicId);
  if (idx === -1) {
    return [];
  }
  return messages
    .slice(Math.max(0, idx - CONTEXT_WINDOW), idx)
    .filter((m) => m.role !== "system" && m.text);
};

// The Retry hover action, handed to the one message allowed to use it.
type RetryAction = {
  // The refusal the last attempt came back with, pinned to `messageId` by the
  // pane so it cannot follow the affordance onto a regenerated turn.
  readonly error: ApiError | null;
  readonly messageId: string;
  readonly onRetry: () => void;
  readonly pending: boolean;
};

// Retry regenerates the agent's *last* turn, so the affordance belongs on the
// newest agent message and nowhere else (chat.md §Copy and Retry) — the route
// refuses any row a `user` message follows, since a later prompt already
// started a turn after this one. A thinking placeholder and a bodiless
// `tool_use` row are skipped: neither renders anything to hang an action on,
// and there is nothing yet to re-run.
const retryTargetId = (
  messages: ReadonlyArray<Message>,
): string | undefined => {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i]!;
    if (message.role === "user") {
      return undefined;
    }
    if (message.role === "agent" && !message.thinking && message.text) {
      return message.id;
    }
  }
  return undefined;
};

// Whether a retry could be accepted at all. The route refuses a task closed to
// a turn and a daemon that is not idle — archived / `cancelled` / `failed`, and
// `working`, `input` or a sandbox still provisioning, all answer `422
// conflict_state` (api.md §Chat retry & task interrupt). Those are the same
// signals the composer reads, so the action is simply absent when the answer is
// already known; a click that races the daemon into a turn is what the error
// handling below is for.
const retryOffered = (status: ApiTaskStatus, canSend: boolean): boolean =>
  canSend &&
  status !== "needs_input" &&
  status !== "pending" &&
  status !== "running";

// Memoised on four primitive props, which costs nothing and stops the pane
// re-rendering for its parent's business. `TaskView` holds live subscriptions
// of its own — the task row on every `task.updated`, and a live diff revision
// every couple of seconds for the whole of a turn — and every one of those
// state changes used to re-render the entire transcript underneath, to keep a
// line count current on a tab the reader is not looking at.
export const ChatTab = memo(function ChatTab({
  canSend,
  repoId,
  taskId,
  taskStatus,
  workspaceId,
}: ChatTabProps) {
  const {
    applyToolCall,
    hasMore,
    loading,
    loadingOlder,
    loadOlder,
    messages,
    newestSequence,
    refetch,
    reload,
  } = useChatMessages(taskId);
  const { inputRequest, refetch: refetchInput } = useTaskInputRequest(taskId);
  // The side-threads drawer's data and the one panel open at a time (fork
  // or reopen — side-threads.md §UX). Both entry points funnel into the same
  // `sidePanel` state so there is only ever one drawer to render.
  const { refetch: refetchThreads, threads } = useSideThreads(taskId);
  const [sidePanel, setSidePanel] = useState<SidePanelState | null>(null);
  // One thread per anchor is the working assumption here (the API allows
  // more, but the UI funnels a second ask on the same message into the
  // existing thread): which message anchors which thread, for the
  // open-instead-of-draft behaviour and the per-message indicator.
  // Archived threads keep their transcript but stop claiming their anchor:
  // the indicator disappears, and forking the message again starts fresh.
  const activeThreads = useMemo(
    () => threads.filter((t) => t.archived_at === null),
    [threads],
  );
  const threadByAnchor = useMemo(() => {
    const map = new Map<string, ApiSideThread>();
    for (const t of activeThreads) {
      map.set(t.anchor.public_id, t);
    }
    return map;
  }, [activeThreads]);
  const anchorIds = useMemo(
    () => new Set(threadByAnchor.keys()),
    [threadByAnchor],
  );
  // Which thread the header badge reopens: the one you last had open on this
  // task, surviving reloads. The list stays a back-arrow away.
  const lastThreadKey = `tin.sideThread.last.${taskId}`;
  // Through a ref so this callback's identity keys on the thread list alone:
  // `openSideThread` reads `messages` freshly, and keying on those would
  // churn every row's memoisation each poll. Changing on the thread list is
  // wanted — the rows' indicators read from the same map (see
  // `ChatMessageContent`'s memoisation comment).
  const openSideThreadRef = useRef<(t: ApiSideThread) => void>(() => {});
  const startSideThread = useCallback(
    (m: Message): void => {
      const existing = threadByAnchor.get(m.id);
      if (existing) {
        openSideThreadRef.current(existing);
        return;
      }
      if (!m.text) {
        return;
      }
      setSidePanel({
        anchor: {
          body: m.text,
          publicId: m.id,
          role: m.role,
          sequence: m.sequence,
          who: speakerName(m),
        },
        kind: "new",
      });
    },
    [threadByAnchor],
  );
  // Reopening from the drawer: the anchor's full row may still be in the
  // loaded primary window (most side threads are recent), in which case it
  // quotes and stales the same as a fresh fork. Otherwise fall back to the
  // body the `GET /threads` row itself carries, with no sequence to compare
  // against — the panel simply omits the staleness note rather than guessing.
  const openSideThread = (t: ApiSideThread): void => {
    const loaded = messages.find((m) => m.id === t.anchor.public_id);
    localStorage.setItem(lastThreadKey, t.public_id);
    setSidePanel({
      anchor: loaded
        ? {
            body: loaded.text ?? t.anchor.body ?? "",
            publicId: loaded.id,
            role: loaded.role,
            sequence: loaded.sequence,
            who: speakerName(loaded),
          }
        : { body: t.anchor.body ?? "", publicId: t.anchor.public_id },
      kind: "existing",
      threadId: t.public_id,
    });
  };
  useLayoutEffect(() => {
    openSideThreadRef.current = openSideThread;
  });
  // Closes the panel and scrolls the primary thread to the anchor row (the
  // staleness note's "jump to primary thread" link).
  const jumpToSideThreadAnchor = (publicId: string): void => {
    setSidePanel(null);
    requestAnimationFrame(() => {
      document
        .getElementById(`msg-${publicId}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };
  // Skipped for a task closed to messages: the menu is suppressed there
  // whatever the answer, so asking is a round trip nothing can use. `canSend`
  // is derived from the task row (TaskView), so a task that reopens — an
  // unarchived `done` one taking a follow-up — fetches when it does.
  const { commands, refetch: refetchCommands } = useAgentCommands(
    canSend ? taskId : null,
  );
  // The turn being streamed: the accumulated text, the `stream_id` it belongs
  // to, the newest sequence the window held as of its latest delta, and the
  // task it is a turn of.
  //
  // That sequence is what ends the caret. The bubble is held until the row
  // that *replaces* it is on screen, rather than being dropped the moment the
  // server announces one — see `streamingText` below.
  //
  // The task rides along so that switching tasks — which this component does
  // without remounting — cannot show one transcript's half-written turn over
  // another's, and so that it needs no effect to say so.
  const [stream, setStream] = useState<{
    readonly id: string;
    readonly sinceSequence: number;
    readonly taskId: string;
    readonly text: string;
  } | null>(null);
  // A provisioning card the server has not finished writing. See the realtime
  // handler below for why the pane cares.
  const unsettledCard = messages.some(
    (m) =>
      m.role === "system" &&
      m.systemKind === "boot" &&
      cardInFlight(m.steps ?? [], taskStatus),
  );
  // Live updates on the task scope: `chat.message.streaming` carries the
  // in-flight assistant text (rendered as a caret bubble); a chat kind means a
  // message or prompt changed, so refetch.
  useRealtimeScope(`task:${taskId}`, (event) => {
    if (event.kind === "chat.message.streaming") {
      const summary = (event.summary ?? {}) as {
        stream_id?: unknown;
        text?: unknown;
      };
      if (typeof summary.text === "string") {
        const text = summary.text;
        // A frame without a `stream_id` folds into one shared stream rather
        // than opening a fresh one per delta — which would reset the bar to
        // the current sequence every time and leave a caret that never ends.
        const id =
          typeof summary.stream_id === "string" ? summary.stream_id : "";
        setStream((prev) => ({
          id,
          // Raised on every delta, not fixed at the first one: the previous
          // turn's row can still be in flight when this turn starts streaming,
          // and it must not end a caret that is still being written. Only a row
          // arriving after the *last* delta does that, and that row is the one
          // this caret was streaming.
          sinceSequence:
            prev && prev.id === id && prev.taskId === taskId
              ? Math.max(prev.sinceSequence, newestSequence)
              : newestSequence,
          taskId,
          text,
        }));
      }
      return;
    }
    // The session's commands changed — a sandbox that has just finished
    // booting and read them for the first time, or a skill it discovered as it
    // worked. The event carries no payload, so this is a cue to re-read them
    // (chat.md §Slash commands); the transcript is untouched, so nothing below
    // applies.
    if (event.kind === "agent.commands.updated") {
      refetchCommands();
      return;
    }
    // A tool call opened or settled. The frame carries the whole card, so it is
    // applied to the window rather than refetched: the card appears the moment
    // the agent makes the call and moves to its finished state in place
    // (chat.md §Tool-call cards). Refetching would not even work for the
    // second half — the row it rewrites sits behind the paging cursor.
    if (event.kind === "chat.tool_call.updated") {
      const update = toolCallUpdate(event.summary);
      if (update) {
        applyToolCall(update);
      }
      // The boot card is repaired by *any* traffic on the scope, and an agent
      // reaching for a tool is proof the provision it is still narrating
      // finished.
      if (unsettledCard) {
        reload();
      }
      return;
    }
    // Everything else on the scope belongs to another surface. The task scope
    // is the widest one there is — build statuses, pull requests, live diff
    // revisions, process and service logs, terminals, tunnels, reviews and
    // intents all arrive on it — and this handler used to name a few kinds and
    // treat the rest as "the chat changed", which cost two round trips apiece
    // and, far worse, deleted the paragraph the reader was in the middle of.
    // A CI round alone is dozens of those (`build-status.md` §Apply emits one
    // per row upsert, heartbeats included).
    //
    // The one thing a foreign event still does is repair a boot card, for the
    // reason given below.
    if (!CHAT_KINDS.has(event.kind)) {
      if (unsettledCard) {
        reload();
      }
      return;
    }
    // A message that changed in place — the provisioning card as it walks its
    // steps — is behind the newest sequence we hold, so paging forward from
    // there would never see it (realtime.md §Event kinds).
    if (event.kind === "chat.message.updated") {
      reload();
      return;
    }
    // …and that is the card's *only* route in, which makes it a single point of
    // failure: `chat.message.updated` is one transient frame, and every other
    // refresh here pages forward from the newest sequence held, which the card
    // sits behind for the whole of its life. Miss one — the first page still in
    // flight when it fires, a socket blip, a `resync` whose gap was too large to
    // replay — and the card is stranded on whichever step it was on. Nothing
    // that arrives afterwards moves it, so the pane fills with the agent's
    // messages above a header that still says the sandbox is being provisioned.
    //
    // So while a card is still being written, re-read the window on *any*
    // event. By the time the agent is talking the provision is over, and its
    // own messages are what repair a card whose last rewrite never landed.
    if (event.kind === "resync" || unsettledCard) {
      reload();
    } else {
      refetch();
    }
    refetchInput();
  });

  // The caret ends when the row that replaces it is on screen — not when the
  // server announces that one exists.
  //
  // `chat.message.appended` used to drop the streamed text and *then* go and
  // fetch the row, so for one round trip the thread showed neither: the
  // paragraph the reader was mid-way through was replaced by the thinking dots
  // and came back a moment later, several hundred pixels shorter and then
  // taller again. Once per message, on every task, with no CI involved.
  //
  // Derived rather than cleared from the handler, so the caret and the row it
  // is standing in for can never both be on screen, or both be missing.
  const streamingText =
    stream && stream.taskId === taskId && newestSequence <= stream.sinceSequence
      ? stream.text
      : null;

  const [draft, setDraft] = usePersistedDraftText(chatDraftTextKey(taskId));
  const [sending, setSending] = useState(false);
  // Why the last attempt never left the composer. The draft and the chips stay
  // put either way, so a retry is one click — but saying nothing about the
  // refusal made that read as a dead button, which is what this is here to
  // stop. Cleared when the next attempt starts, and carrying the task it
  // belongs to for the same reason `stream` above does.
  const [sendFailure, setSendFailure] = useState<{
    readonly message: string;
    readonly taskId: string;
  } | null>(null);
  const [interrupting, setInterrupting] = useState(false);
  // Which completion the arrows are on, and whether Esc has dismissed the menu
  // for the name currently being typed. Dismissal is a per-draft decision, so
  // both reset the moment the draft changes.
  const [commandIndex, setCommandIndex] = useState(0);
  const [commandsDismissed, setCommandsDismissed] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const composerBoxRef = useRef<HTMLDivElement>(null);
  useAutosizeTextarea(composerRef, draft);
  // Both are keyed by the message they belong to rather than held as bare
  // flags: once the retried turn lands the affordance moves to the regenerated
  // message, and a stale refusal must not move with it.
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [retryFailure, setRetryFailure] = useState<{
    error: ApiError;
    messageId: string;
  } | null>(null);
  // Whether the reader is following the thread, which is a decision of
  // theirs and *not* the same question as whether the view is currently at
  // the bottom — a clamp can put them there without their asking, and
  // `onScroll` refuses to read that as consent (see below). The sibling log
  // pane draws the same distinction with the same words (`ServicesTab`:
  // `following` for the decision, a local `atBottom` for the geometry).
  // Drives the "Latest" pill.
  const [following, setFollowing] = useState(true);
  // The same answer in a ref, because the sticky-scroll effect below needs to
  // know whether the view was following *before* the new content (a tool
  // result, say) was appended. Reading DOM measurements after the append is
  // too late: a large tool-result body can grow scrollHeight by more than the
  // slack in one commit, so recomputing it post-append falsely concludes the
  // reader had scrolled away and stops auto-scrolling (the "Latest" pill bug).
  const followingRef = useRef(true);
  const scrollRef = useRef<HTMLDivElement>(null);
  // The offset the last scroll event reported, so the next one can tell which
  // way the view moved. See `onScroll`.
  const lastTopRef = useRef(0);
  // Pre-prepend scrollHeight, set when a scroll-up triggers loadOlder so the
  // view stays anchored to the same message once the older page renders.
  const restoreRef = useRef<number | null>(null);

  // The task whose history we have already pinned to the bottom.
  const pinnedFor = useRef<string | null>(null);

  // Every programmatic scroll goes through here, because `onScroll` tells the
  // reader's scrolling from the browser's by comparing against `lastTopRef` —
  // and a writer that moved the view without refreshing it would leave that
  // comparison describing an offset two moves out of date. Reads the offset
  // back rather than trusting the assignment, since the browser clamps it.
  const setScrollTop = (el: HTMLDivElement, top: number): void => {
    el.scrollTop = top;
    lastTopRef.current = el.scrollTop;
  };

  // Open the tab on the newest message, not the top of the history. The
  // sticky-bottom effect below only fires when the view is *already* near the
  // bottom, and a fresh mount starts at scrollTop 0 with the whole history
  // above it — so without this the first paint lands on the oldest message.
  // A layout effect, so the jump happens before paint rather than as a visible
  // scroll. Keyed on taskId as well as messages: the tab remounts when you
  // switch tabs, but not when you switch tasks underneath it.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || pinnedFor.current === taskId || messages.length === 0) {
      return;
    }
    pinnedFor.current = taskId;
    setScrollTop(el, el.scrollHeight);
  }, [messages, taskId]);

  // Anchor scroll after an older page prepends (before paint, to avoid a jump).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el && restoreRef.current !== null) {
      setScrollTop(el, el.scrollTop + el.scrollHeight - restoreRef.current);
      restoreRef.current = null;
    }
  }, [messages]);

  // Keep a following reader at the bottom as content lands.
  //
  // A layout effect rather than a passive one, matching the anchor above it:
  // the DOM has the new content but the browser has not painted it, so the pin
  // lands in the same frame. Run after paint — as this did — each delta was
  // painted at the *old* offset and corrected a frame later, which is a jump
  // per token for the whole of a turn.
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || restoreRef.current !== null) {
      return;
    }
    if (followingRef.current) {
      setScrollTop(el, el.scrollHeight);
    }
  }, [messages, streamingText]);

  const onScroll = (): void => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    const top = el.scrollTop;
    const movedDown = top > lastTopRef.current;
    lastTopRef.current = top;
    const nearBottom =
      el.scrollHeight - top - el.clientHeight < FOLLOW_SLACK_PX;
    // Following is the reader's decision, so only the reader may re-take it.
    //
    // This handler cannot tell their scrolling from the browser's own. When
    // content *below* the viewport shrinks — a caret replaced, a tool card
    // settling — the offset they chose stops being reachable and the browser
    // clamps it downwards, firing an ordinary scroll event as it does. Read
    // naively, that geometry says "at the bottom", so the pin re-engaged and
    // the next message threw the reader to the end of a transcript they were
    // deliberately scrolled back in. That is the bug this pane was reported
    // for, and it needs no CI to happen — every message ends with one.
    //
    // A clamp only ever *lowers* scrollTop. Requiring a downward move to
    // re-pin is therefore enough to tell the two apart: a reader scrolling
    // towards the bottom raises it, and nothing else does.
    const pinned = nearBottom && (followingRef.current || movedDown);
    followingRef.current = pinned;
    setFollowing(pinned);
    // Near the top with more history: pull the previous page, remembering the
    // current height so the layout effect can re-anchor the scroll.
    if (top < PAGE_BACK_PX && hasMore && !loadingOlder) {
      restoreRef.current = el.scrollHeight;
      loadOlder();
    }
  };
  const jumpToLatest = (): void => {
    const el = scrollRef.current;
    if (el) {
      // Said outright rather than inferred from the scroll this provokes:
      // clicking Latest is the one unambiguous "follow along again".
      followingRef.current = true;
      setFollowing(true);
      setScrollTop(el, el.scrollHeight);
    }
  };

  // ---- Composer attachments (chat.md §Composer UI) -----------------------
  const attachEnabled = workspaceId !== undefined && canSend;
  const {
    addFiles,
    attachments,
    clear: clearAttachments,
    dragOver,
    readyAttachments,
    removeChip,
    retryChip,
    setDragOver,
    uploadsSettled,
  } = useComposerAttachments(
    workspaceId,
    canSend,
    chatDraftAttachmentsKey(taskId),
  );
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A message needs words or files (chat.md §Composer): a pasted screenshot
  // with no caption sends. Send waits for every chip to settle — a file still
  // uploading is not raced, and a failed one is not silently dropped; its
  // chip says why and offers retry or remove.
  const hasContent = draft.trim().length > 0 || readyAttachments.length > 0;
  const canSubmit = canSend && !sending && uploadsSettled && hasContent;

  // The composer floats over the scroll area, and the 140px the stylesheet
  // reserves for it only covers its resting height. As it grows — a long
  // draft, attachment chips — pad the scroll area to match so the newest
  // messages move up instead of disappearing behind it, and keep the view
  // pinned if the reader was following the bottom.
  useLayoutEffect(() => {
    const chat = scrollRef.current;
    const box = composerBoxRef.current;
    if (!chat || !box) {
      return;
    }
    const nearBottom =
      chat.scrollHeight - chat.scrollTop - chat.clientHeight < FOLLOW_SLACK_PX;
    chat.style.paddingBottom = `${Math.max(140, box.offsetHeight + 32)}px`;
    if (nearBottom) {
      setScrollTop(chat, chat.scrollHeight);
    }
  }, [draft, attachments]);

  const send = (): void => {
    const text = draft.trim();
    if (!canSubmit) {
      return;
    }
    setSending(true);
    setSendFailure(null);
    void sendChatMessage(
      taskId,
      text,
      readyAttachments.map((a) => a.uploaded!.public_id),
    )
      .then(() => {
        clearAttachments();
        setDraft("");
        refetch();
      })
      .catch((err: unknown) => {
        // The draft and the chips stay, so the retry is the same click again.
        setSendFailure({
          message:
            err instanceof ApiError
              ? err.message
              : "Couldn't send that message.",
          taskId,
        });
      })
      .finally(() => setSending(false));
  };

  // A retry writes nothing: the 202 only says the turn's originating prompt
  // went back out, and the regenerated rows arrive as ordinary appended
  // messages over realtime (chat.md §Copy and Retry). So there is nothing to
  // refetch on success, and — chat being append-only — nothing above or below
  // the retried turn is touched; the new output simply lands after it.
  const retryTurn = (messageId: string): void => {
    if (retryingId !== null) {
      return;
    }
    setRetryingId(messageId);
    setRetryFailure(null);
    void retryAgentTurn(taskId, messageId)
      .catch((err: unknown) => {
        setRetryFailure({
          error:
            err instanceof ApiError
              ? err
              : new ApiError("unknown", "Couldn't re-run that turn.", 0),
          messageId,
        });
      })
      .finally(() => setRetryingId(null));
  };

  // Best-effort: interruptTask converges the task/daemon state regardless, and
  // the resulting task.updated event (see the route) is what actually clears
  // the working cue, not this call resolving.
  const stop = (): void => {
    if (interrupting) {
      return;
    }
    setInterrupting(true);
    void interruptTask(taskId).finally(() => setInterrupting(false));
  };

  const placeholder = composerPlaceholder(taskStatus, canSend);

  // The `/` menu. Open while the draft is naming a command, the session has one
  // to answer with, and the reader has not dismissed it for this draft. A task
  // closed to messages has no composer to complete into, so it has no menu
  // either.
  const fragment = commandFragment(draft);
  const matches =
    fragment === null || commandsDismissed || !canSend
      ? []
      : matchCommands(commands, fragment);
  const activeCommand = Math.min(commandIndex, Math.max(matches.length - 1, 0));

  // Every keystroke re-opens the menu: dismissing it is a decision about the
  // name on screen, and typing another character is a different name.
  const changeDraft = (text: string): void => {
    setDraft(text);
    setCommandIndex(0);
    setCommandsDismissed(false);
  };

  // Completing settles the name, so the menu closes rather than re-offering the
  // command that was just picked. Focus goes back to the textarea, which a
  // click on the menu never took but a keyboard user never left.
  const complete = (command: ApiAgentCommand): void => {
    setDraft(completeCommand(command));
    setCommandIndex(0);
    setCommandsDismissed(true);
    composerRef.current?.focus();
  };

  // The composer's keymap (frontend.md §Keymap). While the `/` menu is open it
  // takes the keys it needs and the composer keeps the rest — including Enter,
  // which picks the highlighted command rather than sending a half-typed name.
  // Sending is then the next Enter, on a draft that says what it will do, which
  // is why one predicate answers both.
  const onComposerKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>): void => {
    const submits =
      e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing;
    if (matches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setCommandIndex(Math.min(activeCommand + 1, matches.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setCommandIndex(Math.max(activeCommand - 1, 0));
      } else if (e.key === "Escape") {
        e.preventDefault();
        setCommandsDismissed(true);
      } else if (e.key === "Tab" || submits) {
        e.preventDefault();
        complete(matches[activeCommand]!);
      }
      return;
    }
    if (submits) {
      e.preventDefault();
      send();
    }
  };

  // A working cue at the bottom of the thread whenever the agent is actively
  // on a turn (taskStatus "running" is the daemon's "working" state — see
  // composerPlaceholder above / chat.md §Composer). The server's own thinking
  // placeholder (chat.md §Thinking placeholder) only covers the start of a
  // turn, before any text has streamed; once a tool call finishes and the
  // agent is composing its next step, nothing else at the bottom of the list
  // signals that it's still working. Suppressed when a streaming caret is
  // already live, or the last message is already that thinking placeholder,
  // so the dots never double up.
  const lastMessage = messages[messages.length - 1];
  const showWorking =
    taskStatus === "running" &&
    streamingText === null &&
    !lastMessage?.thinking;
  const visibleMessages: ReadonlyArray<Message> = [
    ...messages,
    ...(streamingText !== null
      ? [
          {
            id: "__streaming",
            role: "agent" as const,
            streaming: true,
            text: streamingText,
            ts: "now",
          },
        ]
      : []),
    ...(showWorking
      ? [
          {
            id: "__working",
            role: "agent" as const,
            thinking: true,
            ts: "now",
          },
        ]
      : []),
  ];
  const messageGroups = groupMessages(visibleMessages);
  // Off `messages`, not `visibleMessages`: the streaming and working rows are
  // client-side cues with no persisted row behind them, so there is no id to
  // retry (chat.md §Streaming caret).
  const retryId = retryOffered(taskStatus, canSend)
    ? retryTargetId(messages)
    : undefined;
  const retry: RetryAction | undefined =
    retryId === undefined
      ? undefined
      : {
          error:
            retryFailure?.messageId === retryId ? retryFailure.error : null,
          messageId: retryId,
          onRetry: () => retryTurn(retryId),
          pending: retryingId === retryId,
        };

  return (
    <div className={styles.root}>
      {threads.length > 0 ? (
        <button
          className={styles.threadsRail}
          onClick={() => {
            // Straight back into the conversation you were in the middle of;
            // the list is the panel's back arrow, for switching or scanning.
            const last = localStorage.getItem(lastThreadKey);
            const lastThread = activeThreads.find((t) => t.public_id === last);
            if (lastThread) {
              openSideThread(lastThread);
            } else {
              setSidePanel({ kind: "list" });
            }
          }}
          type="button"
        >
          <Icon.Comment size={12} />{" "}
          {activeThreads.length > 0
            ? `${String(activeThreads.length)} side thread${activeThreads.length === 1 ? "" : "s"}`
            : "archived threads"}
          {activeThreads.some(isSideThreadUnread) ? (
            <span className={styles.threadsRailUnread} title="New reply" />
          ) : null}
        </button>
      ) : null}
      <div className={styles.chat} onScroll={onScroll} ref={scrollRef}>
        <div className={styles.col}>
          {loadingOlder ? (
            <div className={styles.chatLoading}>Loading earlier messages…</div>
          ) : null}
          {loading && messages.length === 0 ? (
            <div className={styles.chatLoading}>Loading conversation…</div>
          ) : (
            messageGroups.map((group) => (
              <ChatMessageGroup
                key={group[0]!.id}
                messages={group}
                onStartSideThread={startSideThread}
                onStop={stop}
                repoId={repoId}
                retry={retry}
                sideThreadAnchors={anchorIds}
                stopping={interrupting}
                taskStatus={taskStatus}
              />
            ))
          )}
          {inputRequest ? (
            <InputRequestPrompt
              // Answered or stopped, the request has left `pending` either way
              // and the card re-reads itself away.
              onResolved={() => refetchInput()}
              request={inputRequest}
              taskId={taskId}
            />
          ) : null}
        </div>
      </div>

      {!following && (
        <button
          className={styles.jump}
          onClick={jumpToLatest}
          title="Jump to latest"
          type="button"
        >
          <Icon.ArrowDown size={14} /> Latest
        </button>
      )}

      <div
        className={cx(styles.composer, dragOver && styles.composerDrop)}
        onDragLeave={() => setDragOver(false)}
        onDragOver={(e) => {
          if (attachEnabled && e.dataTransfer.types.includes("Files")) {
            e.preventDefault();
            setDragOver(true);
          }
        }}
        onDrop={(e) => {
          if (!attachEnabled) {
            return;
          }
          e.preventDefault();
          setDragOver(false);
          addFiles(e.dataTransfer.files);
        }}
        ref={composerBoxRef}
      >
        {matches.length > 0 ? (
          <SlashCommandMenu
            active={activeCommand}
            commands={matches}
            onHover={setCommandIndex}
            onPick={complete}
          />
        ) : null}
        {sendFailure?.taskId === taskId ? (
          <div className={styles.composerError} role="alert">
            {sendFailure.message}
          </div>
        ) : null}
        <AttachmentChips
          attachments={attachments}
          onRemove={removeChip}
          onRetry={retryChip}
        />
        <div className={styles.composerInner}>
          <textarea
            disabled={!canSend || sending}
            onChange={(e) => changeDraft(e.target.value)}
            onKeyDown={onComposerKeyDown}
            onPaste={(e) => {
              if (!attachEnabled) {
                return;
              }
              // The cmd+V-a-screenshot case: clipboard files become chips;
              // pasted text falls through to the textarea as ever.
              const files = Array.from(e.clipboardData.files);
              if (files.length > 0) {
                e.preventDefault();
                addFiles(files);
              }
            }}
            placeholder={placeholder}
            ref={composerRef}
            value={draft}
          />
          <div className={styles.composerButtons}>
            {attachEnabled ? (
              <>
                <input
                  hidden
                  multiple
                  onChange={(e) => {
                    addFiles(e.target.files ?? []);
                    e.target.value = "";
                  }}
                  ref={fileInputRef}
                  type="file"
                />
                <Button
                  icon
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach files"
                  variant="ghost"
                >
                  <Icon.Paperclip size={14} />
                </Button>
              </>
            ) : null}
            {/* Sending is said on the button, not just by the greyed-out
                composer: a send that is merely slow otherwise looks exactly
                like a button that ignored the click. */}
            <Button
              disabled={!canSubmit}
              icon
              onClick={send}
              style={{ opacity: canSubmit || sending ? 1 : 0.5 }}
              title={
                sending
                  ? "Sending…"
                  : uploadsSettled
                    ? "Send"
                    : "Waiting for attachments — remove or retry failed ones to send"
              }
              variant="accent"
            >
              {sending ? <Icon.Dot3 size={14} /> : <Icon.ArrowUp size={14} />}
            </Button>
          </div>
        </div>
      </div>

      {sidePanel?.kind === "list" ? (
        <SideThreadsList
          onClose={() => setSidePanel(null)}
          onOpen={openSideThread}
          taskId={taskId}
          threads={threads}
        />
      ) : sidePanel ? (
        <SideThreadPanel
          anchor={sidePanel.anchor}
          contextMessages={anchorContext(sidePanel.anchor.publicId, messages)}
          onBack={
            threads.length > 0
              ? () => setSidePanel({ kind: "list" })
              : undefined
          }
          onClose={() => setSidePanel(null)}
          onCreated={(threadId) => {
            localStorage.setItem(lastThreadKey, threadId);
            setSidePanel((prev) =>
              prev && prev.kind === "new"
                ? { anchor: prev.anchor, kind: "existing", threadId }
                : prev,
            );
            refetchThreads();
          }}
          onJumpToAnchor={() =>
            jumpToSideThreadAnchor(sidePanel.anchor.publicId)
          }
          primaryNewestSequence={newestSequence}
          taskId={taskId}
          thread={
            sidePanel.kind === "existing"
              ? threads.find((t) => t.public_id === sidePanel.threadId)
              : undefined
          }
          threadId={sidePanel.kind === "existing" ? sidePanel.threadId : null}
          workspaceId={workspaceId}
        />
      ) : null}
    </div>
  );
});

type ChatMessageGroupProps = {
  readonly messages: ReadonlyArray<Message>;
  readonly onStartSideThread: (m: Message) => void;
  readonly onStop: () => void;
  readonly repoId?: string;
  readonly retry?: RetryAction;
  // Message ids that already anchor a side thread — drives the row's
  // always-visible indicator and turns its fork action into an open.
  readonly sideThreadAnchors: ReadonlySet<string>;
  readonly stopping?: boolean;
  readonly taskStatus?: ApiTaskStatus;
};

const ChatMessageGroup = ({
  messages,
  onStartSideThread,
  onStop,
  repoId,
  retry,
  sideThreadAnchors,
  stopping,
  taskStatus,
}: ChatMessageGroupProps) => {
  const first = messages[0]!;
  if (first.role === "system") {
    return <SystemEvent m={first} taskStatus={taskStatus} />;
  }
  // This task's agent, or another task's — whose task message is drawn as an
  // agent and linked to its task, never as the owner it is authored under.
  const { fromTask } = first;
  const asAgent = first.role === "agent" || fromTask !== undefined;
  const name =
    first.role === "agent" ? APP_NAME : (first.who ?? "Unknown user");
  const fromTaskPath =
    fromTask && repoId ? paths.task(repoId, fromTask.label) : undefined;
  const parts = groupParts(messages);
  return (
    <div className={styles.msg}>
      <div className={cx(styles.avatar, asAgent && styles.agent)}>
        {asAgent ? (
          "T"
        ) : (
          <Avatar initials={first.avatarInitials} name={name} size={28} />
        )}
      </div>
      <div className={styles.body}>
        <div className={styles.name}>
          {fromTaskPath ? (
            <Link className={styles.nameAgent} to={fromTaskPath}>
              {name}
            </Link>
          ) : (
            <span className={asAgent ? styles.nameAgent : undefined}>
              {name}
            </span>
          )}
          <span className={styles.ts}>{first.ts}</span>
        </div>
        <div className={styles.messageParts}>
          {parts.map((part, index) =>
            part.kind === "tools" ? (
              <ToolCallBlock key={part.key} tools={part.tools} />
            ) : (
              <ChatMessageContent
                hasSideThread={sideThreadAnchors.has(part.message.id)}
                key={part.key}
                m={part.message}
                onStartSideThread={onStartSideThread}
                // Only the final row can carry the live thinking cue. A stop
                // button on an older row would interrupt a later turn.
                onStop={
                  part.message.thinking &&
                  index === parts.length - 1 &&
                  taskStatus === "running"
                    ? onStop
                    : undefined
                }
                retry={retry?.messageId === part.message.id ? retry : undefined}
                stopping={stopping}
              />
            ),
          )}
        </div>
      </div>
    </div>
  );
};

// Memoised, and the one place in the pane where that earns its keep: this is
// what renders `<Markdown>`, and re-parsing a whole window of them was costing
// ~20ms per streamed token on a sixty-message transcript — more than a frame,
// for a change confined to the bubble at the end. `m` keeps its identity
// across a refetch that did not touch its row (see `useChatMessages`, which
// re-maps only when the row changed or its relative timestamp went stale), and
// `onStop`/`retry` are undefined for every message but the last — so the
// comparison holds for the whole transcript.
const ChatMessageContent = memo(function ChatMessageContent({
  hasSideThread,
  m,
  onStartSideThread,
  onStop,
  retry,
  stopping,
}: {
  readonly hasSideThread?: boolean;
  readonly m: Message;
  readonly onStartSideThread?: (m: Message) => void;
  readonly onStop?: () => void;
  readonly retry?: RetryAction;
  readonly stopping?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = (): void => {
    void navigator.clipboard?.writeText(m.text ?? "");
    setCopied(true);
    setTimeout(() => setCopied(false), 1400);
  };
  return (
    // The id a side thread's "jump to primary thread" link scrolls to
    // (side-threads.md §UX) — every row that can be a side thread's anchor
    // gets one, since any of them can become one.
    <div className={styles.messagePart} id={`msg-${m.id}`}>
      <div className={styles.messagePartBody}>
        {m.thinking ? (
          <span className={styles.thinkingRow}>
            <span className="thinking">
              <span className="dot" />
              <span className="dot" />
              <span className="dot" /> hammering it out…
            </span>
            {onStop ? (
              <button
                className={styles.stopBtn}
                disabled={stopping}
                onClick={onStop}
                title="Stop"
                type="button"
              >
                <Icon.Stop size={10} />
              </button>
            ) : null}
          </span>
        ) : (
          <div className={styles.text}>
            <Markdown text={m.text ?? ""} />
            {m.streaming ? <span className="stream-caret" /> : null}
            {m.attachments?.length ? (
              <MessageAttachments attachments={m.attachments} />
            ) : null}
          </div>
        )}
        {/* The turn takes a moment to come back, and until the daemon's first
            frame lands nothing else in the thread says a re-run is under way. */}
        {retry?.pending ? (
          <div className={styles.retryNote}>Re-running this turn…</div>
        ) : null}
        {retry?.error ? (
          <div className={styles.retryError} role="alert">
            <span>{retry.error.message}</span>
            {/* A `422 conflict_state` is the task's own state refusing: the
                daemon is mid-turn, or the thread has moved past this message.
                Clicking again changes nothing until that does, so no second
                attempt is offered. Anything else — a `503 sandbox_unavailable`
                above all, which means the prompt never reached the sandbox and
                nothing was written — may well work on the next try (api.md
                §Chat retry & task interrupt). */}
            {retry.error.code === "conflict_state" ? null : (
              <button
                className={styles.retryAgain}
                disabled={retry.pending}
                onClick={retry.onRetry}
                type="button"
              >
                Try again
              </button>
            )}
          </div>
        ) : null}
        {hasSideThread && onStartSideThread ? (
          <button
            className={styles.threadMark}
            onClick={() => onStartSideThread(m)}
            type="button"
          >
            <Icon.Comment size={11} />
            side thread
          </button>
        ) : null}
      </div>
      {!m.thinking && m.text ? (
        <span className={styles.actions}>
          <button
            onClick={copy}
            title={copied ? "Copied" : "Copy"}
            type="button"
          >
            {copied ? <Icon.Check size={12} /> : <Icon.Copy size={12} />}
          </button>
          {/* Every primary-thread message can fork a side thread
              (side-threads.md §UX) — unlike Retry, this isn't limited to the
              latest turn. */}
          {onStartSideThread ? (
            <button
              onClick={() => onStartSideThread(m)}
              title={hasSideThread ? "Open side thread" : "Start a side thread"}
              type="button"
            >
              <Icon.Comment size={12} />
            </button>
          ) : null}
          {/* Only ever on the latest agent message: Retry re-runs the last turn
              (chat.md §Copy and Retry), and the regenerated output lands after
              this one rather than replacing it. */}
          {retry ? (
            <button
              disabled={retry.pending}
              onClick={retry.onRetry}
              title={retry.pending ? "Retrying…" : "Retry"}
              type="button"
            >
              <Icon.Refresh size={12} />
            </button>
          ) : null}
        </span>
      ) : null}
    </div>
  );
});

// The Agent SDK's own tools, named exactly as the SDK emits them in its
// `tool_use` blocks — these strings are the wire format, not labels, so they
// are matched verbatim (code.claude.com/docs/en/tools-reference).
//
// They earn the quiet density because the header already tells the whole
// story: `Read src/index.ts` has nothing behind it a reader wants that the
// path doesn't already say, and `Bash pnpm lint` is read for the fact that
// lint ran — when its output matters the agent's own prose says so, in the
// sentence right below. What keeps the card is the call whose *result* is
// what the reader came for and has no other home: a subagent's report, an
// integration's response, anything from an MCP server. An unknown name
// falling through to the card is the safe direction: a body nobody opens
// costs a chevron, a body that can't be opened costs the information.
const QUIET_TOOLS: ReadonlySet<string> = new Set([
  "Bash",
  "Edit",
  "EnterPlanMode",
  "EnterWorktree",
  "ExitPlanMode",
  "ExitWorktree",
  "Glob",
  "Grep",
  "ListAgents",
  "NotebookEdit",
  "Read",
  "ScheduleWakeup",
  "TaskCreate",
  "TaskUpdate",
  "ToolSearch",
  "WebFetch",
  "WebSearch",
  "Write",
]);

// A call that came back with a picture is never quiet, whichever tool made it:
// the image is the result, and the quiet density has nowhere to put one — no
// body, no disclosure, nothing but a path (chat.md §Tool calls that return an
// image). `Read src/logo.png` is what makes this more than a browser rule.
const quiet = (tool: ToolCall): boolean =>
  QUIET_TOOLS.has(tool.name) && tool.images.length === 0;

// A run's elapsed time is the sum of its calls'. Null unless something was
// measured, so an all-unmeasured run stays blank instead of claiming "0ms".
const runDuration = (tools: ReadonlyArray<ToolCall>): number | null =>
  tools.reduce<number | null>(
    (total, tool) =>
      tool.durationMs === null ? total : (total ?? 0) + tool.durationMs,
    null,
  );

// A call may join the line above it only if that line is already the quiet
// density and both calls succeeded. Anything else is the one thing the reader
// is scanning for: a failure, a cancellation, or a call still running must keep
// its own line rather than disappear into a count (chat.md §Tool-call cards).
const collapsible = (tool: ToolCall): boolean =>
  quiet(tool) && (tool.status ?? "succeeded") === "succeeded";

// Split a block's calls into runs of the same quiet tool. A run of one renders
// exactly as it did before; a longer one collapses to a single line.
const toolRuns = (
  tools: ReadonlyArray<ToolCall>,
): ReadonlyArray<ReadonlyArray<ToolCall>> => {
  const runs: ToolCall[][] = [];
  for (const tool of tools) {
    const previous = runs.at(-1);
    const head = previous?.[0];
    if (
      previous &&
      head &&
      head.name === tool.name &&
      collapsible(head) &&
      collapsible(tool)
    ) {
      previous.push(tool);
    } else {
      runs.push([tool]);
    }
  }
  return runs;
};

// The tool calls of one or more adjacent rows, rendered as a single block so
// consecutive lines read as a list rather than as separate message parts.
const ToolCallBlock = ({
  tools,
}: {
  readonly tools: ReadonlyArray<ToolCall>;
}) => (
  <div className={styles.messagePart}>
    <div className={cx(styles.messagePartBody, styles.toolBlock)}>
      {toolRuns(tools).map((run, i) =>
        run.length > 1 ? (
          <ToolCallRun key={i} tools={run} />
        ) : (
          <ToolCallRow key={i} tool={run[0]!} />
        ),
      )}
    </div>
  </div>
);

const ToolCallRow = ({ tool }: { readonly tool: ToolCall }) =>
  quiet(tool) ? <QuietToolCall tool={tool} /> : <ToolCallCard tool={tool} />;

// The quiet line, and what it opens to (chat.md §Tool-call cards). Default
// closed: the header is what a turn is scanned by, and a screen of open bodies
// buries the agent's own prose between them.
//
// A call with no `public_id` cannot be expanded — nothing to fetch the
// arguments by — so it renders as the flat row it always was.
const QuietToolCall = ({ tool }: { readonly tool: ToolCall }) => {
  const [open, setOpen] = useState(false);
  return (
    <ToolCallLine
      expanded={open}
      onToggle={tool.publicId ? () => setOpen(!open) : undefined}
      tool={tool}
    >
      <ToolCallDetail tool={tool} />
    </ToolCallLine>
  );
};

// Reading six files is one thought, not six. A run of the same quiet tool
// collapses to the line the first call would have rendered, with the others
// behind a count and their time added to the duration — the tool name and the
// first path still say what happened, and the block stops crowding out the
// agent's own prose. Every argument stays reachable as the hover title.
//
// Opening it unfolds the run into its calls, each of which opens in turn. The
// header drops the first path while open: it is the row above six rows that
// include it, and repeating it there reads as a seventh call.
const ToolCallRun = ({
  tools,
}: {
  readonly tools: ReadonlyArray<ToolCall>;
}) => {
  const [open, setOpen] = useState(false);
  const head = tools[0]!;
  return (
    <ToolCallLine
      expanded={open}
      more={open ? undefined : tools.length - 1}
      onToggle={() => setOpen(!open)}
      title={tools.map((tool) => tool.arg).join("\n")}
      tool={{
        ...head,
        arg: open ? `${tools.length.toString()} calls` : head.arg,
        durationMs: runDuration(tools),
      }}
    >
      {/* Keyed by the call, not its slot: an unfolded row carries state of its
          own — whether it is open, and the arguments fetched for it — and the
          run grows as later calls settle into it. */}
      {tools.map((tool, i) => (
        <ToolCallRow key={tool.publicId ?? i} tool={tool} />
      ))}
    </ToolCallLine>
  );
};

// Default-closed (chat.md §Tool-call cards): a turn is mostly tool calls, and
// expanding every result body by default buries the agent's own prose. The
// header alone (name · arg · status · duration) says what ran; a click opens
// the body for the one call the reader actually cares about.
//
// A call that returned an image is the exception and opens itself, because a
// picture behind a default-closed chevron is one the reader never learns is
// there. Opening the card rather than hanging the thumbnails outside the
// disclosure buys that for free and keeps the chevron honest: everything the
// card holds is behind it, so closing one really does close it.
const ToolCallCard = ({ tool }: { readonly tool: ToolCall }) => {
  // Null until the reader takes a view, because the images decide the default
  // and they arrive late — a screenshot's card is on screen, running and
  // empty, before `chat.tool_call.updated` brings the bytes. Deriving the
  // default lets that update open the card, while a reader who has already
  // clicked keeps whichever state they chose.
  const [openedByReader, setOpenedByReader] = useState<boolean | null>(null);
  const open = openedByReader ?? tool.images.length > 0;
  const status = tool.status ?? "succeeded";
  const label = toolLabel(tool.name);
  return (
    <div className={styles.tool}>
      <button
        className={styles.toolHead}
        onClick={() => setOpenedByReader(!open)}
        type="button"
      >
        {status === "running" ? (
          <span aria-label="running" className="thinking" title="running">
            <span className="dot" />
          </span>
        ) : (
          <span
            aria-label={status}
            style={{
              background: TOOL_STATUS_COLOR[status],
              borderRadius: "50%",
              flex: "none",
              height: 7,
              width: 7,
            }}
            title={status}
          />
        )}
        <ToolName className={styles.toolLbl} name={tool.name} />
        <span className={styles.toolArg}>{tool.arg}</span>
        <div className={styles.toolGrow} />
        <span className={styles.toolDuration}>{toolDuration(tool)}</span>
        <Icon.ChevDown
          size={12}
          style={{
            color: "var(--ink-3)",
            transform: open ? "rotate(0)" : "rotate(-90deg)",
            transition: "transform .15s",
          }}
        />
      </button>
      {/* Above the text body, and like it behind the chevron: a screenshot is
          glanced at, not read, so it leads. The card being open by default is
          what keeps it from hiding. */}
      {open && tool.images.length > 0 ? (
        <div className={styles.toolImages}>
          {tool.images.map((image) => (
            <ThreadMedia
              alt={`Image returned by ${label}`}
              bound="card"
              bytes={image.bytes}
              filename={toolCallImageFilename(label, image.mime)}
              key={image.publicId}
              kind="image"
              name={label}
              src={toolCallImagePath(image.publicId)}
              title={`${label} — ${formatBytes(image.bytes)}`}
            />
          ))}
        </div>
      ) : null}
      {open && tool.body ? (
        <div className={styles.toolBody}>{tool.body}</div>
      ) : null}
    </div>
  );
};

// Kinds whose card is a single verdict rather than a narrative: one step that
// *is* the whole event. They render flat — see `VerdictEvent` (chat.md
// §System-event cards). A secret rotation is the same shape as a build status:
// one name, one outcome, nothing behind a triangle to reveal. So are the two
// sandbox-liveness kinds — a reconnect is "back after 12s" and a runner restart
// is one host's name — where a rollup would read "1/1 steps" and disclose the
// line already on the row.
const FLAT_KINDS = new Set([
  "build_status",
  "reconnect",
  "runner_restart",
  "secret_rotation",
]);

// A build status behind a disclosure triangle reads as "SYSTEM ci/build 1/1
// steps" — the name of a check and an arithmetic fact, with the one thing the
// reader came for (did it pass?) one click away. There is nothing else in the
// card to reveal, so there is nothing for the click to be for: the ✓/✕ and the
// outcome move into the row itself and the row stops being a button.
const VerdictEvent = ({
  m,
  step,
}: {
  readonly m: Message;
  readonly step: SystemStep;
}) => {
  // The step label repeats the title on a build status (both are the check's
  // context); anywhere it carries something else, keep it.
  const detail = [
    step.label === m.title ? null : step.label,
    step.value,
    step.duration,
  ]
    .map(said)
    .filter((part) => part !== undefined)
    .join(" · ");
  // A skipped check never ran: a ✓ would claim a pass it did not earn.
  const [label, tone, glyph] = step.skipped
    ? ["skipped", styles.skipped, ""]
    : step.ok
      ? ["passed", styles.ok, "✓"]
      : ["failed", styles.fail, "×"];
  return (
    <div className={styles.sysmsg}>
      <div className={styles.sysBody}>
        <div className={cx(styles.sysHead, styles.sysHeadStatic)}>
          <span className={styles.sysKind}>SYSTEM</span>
          <span aria-label={label} className={cx(styles.sysStepIcon, tone)}>
            {glyph}
          </span>
          <span className={styles.sysTitle}>{m.title}</span>
          {detail ? (
            <span className={styles.sysResult} title={detail}>
              {detail}
            </span>
          ) : null}
          {m.ts ? <span className={styles.sysTs}>{m.ts}</span> : null}
        </div>
      </div>
    </div>
  );
};

const SystemEvent = ({
  m,
  taskStatus,
}: {
  readonly m: Message;
  readonly taskStatus?: ApiTaskStatus;
}) => {
  const verdict =
    m.systemKind && FLAT_KINDS.has(m.systemKind) ? m.steps?.[0] : undefined;
  return verdict ? (
    <VerdictEvent m={m} step={verdict} />
  ) : (
    <StepsEvent m={m} taskStatus={taskStatus} />
  );
};

// The multi-stage card: a provision walking through container → clone →
// bootstrap, where the rollup is the summary and the steps are the detail.
const StepsEvent = ({
  m,
  taskStatus,
}: {
  readonly m: Message;
  readonly taskStatus?: ApiTaskStatus;
}) => {
  const steps = m.steps ?? [];
  // A pending step has no outcome yet, so it is neither passed nor failed:
  // counting `!ok` alone painted the running step red and every card mid-flight
  // as a failure.
  const failed = steps.some((s) => !s.ok && !s.pending);
  // The provisioning card is still being written while a step is pending, or
  // while the task is `pending` — it is the same row, re-stepped as each stage
  // completes.
  const inProgress = cardInFlight(steps, taskStatus);
  // Default-closed (chat.md §System-event cards), with two exceptions. While a
  // provision is running, the steps *are* the status — collapsing them leaves
  // the user watching a spinner with no idea which stage is slow. And when one
  // failed, the failed step and its error are the whole reason to look.
  // Derived rather than stored, so the card follows the provision it describes:
  // open while it runs or if it failed, closed once it succeeded — without an
  // effect racing the render. An explicit click wins from then on.
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? (failed || inProgress);
  // How long the thing took, which is not the sum of every row: a nested step
  // ran *inside* the one above it — the runner's services and launch commands
  // all run within `Create sandbox` — so counting both would report a two
  // minute provision as four.
  const totalDuration = steps.reduce((acc, s) => {
    const v = parseFloat(s.duration);
    return Number.isFinite(v) && !s.nested ? acc + v : acc;
  }, 0);
  const okCount = steps.filter((s) => s.ok && !s.pending).length;
  return (
    <div className={styles.sysmsg}>
      <div className={styles.sysBody}>
        <button
          className={styles.sysHead}
          onClick={() => setOverride(!open)}
          type="button"
        >
          <span className={styles.sysKind}>SYSTEM</span>
          <span className={styles.sysTitle}>{m.title}</span>
          {steps.length > 0 ? (
            <span className={styles.sysSummary}>
              {okCount}/{steps.length} steps ·{" "}
              {totalDuration ? `${totalDuration.toFixed(1)}s` : "—"}
            </span>
          ) : null}
          {m.ts ? <span className={styles.sysTs}>{m.ts}</span> : null}
          {steps.length > 0 ? (
            <Icon.ChevDown
              size={12}
              style={{
                color: "var(--ink-3)",
                transform: open ? "rotate(0)" : "rotate(-90deg)",
                transition: "transform .15s",
              }}
            />
          ) : null}
        </button>
        {open && steps.length > 0 ? (
          <ol className={styles.sysSteps}>
            {steps.map((s, i) => (
              <StepRow key={i} step={s} />
            ))}
          </ol>
        ) : null}
      </div>
    </div>
  );
};

/**
 * Anything worth printing, or nothing at all.
 *
 * A step's unset fields arrive as the em dash the card renders for "no value"
 * (`bootEvents.ts` §BootStep), so dropping one is not the same as dropping an
 * empty string and both card shapes have to agree on it.
 */
const said = (text: string | null | undefined): string | undefined =>
  text && text !== "—" ? text : undefined;

// One step of a narrative card. A component of its own because the block under
// the row remembers whether it has been opened, and a card can hold a dozen of
// them: `pnpm install` for a repository that declares one gets a row here.
const StepRow = ({ step }: { readonly step: SystemStep }) => {
  // Same rule the card's own rollup uses: a pending step has no outcome yet, so
  // it is neither a pass to summarise nor a failure to explain.
  const failed = !step.ok && !step.pending;
  // What goes under the row: what the step printed, or — for a step that has
  // nothing but an error — the error. Never both, because on a launch command
  // the two are the same failure said twice. Whichever is left stays on the
  // line, where a short summary belongs.
  const value = said(step.value);
  const block = said(step.output) ?? (failed ? value : undefined);
  const inline = value === block ? undefined : value;
  // Open when it failed: a failed step's output is the whole reason to look at
  // the card, and everything else is there if you want it. Derived rather than
  // stored so a step that fails while being read opens itself; a click wins
  // from then on, as on the card above.
  const [override, setOverride] = useState<boolean | null>(null);
  const open = override ?? failed;
  return (
    <li
      className={cx(
        styles.sysStep,
        step.nested && styles.sysStepNested,
        step.pending && styles.sysStepNow,
      )}
    >
      <div className={styles.sysStepRow}>
        {/* The running step gets a marker of its own rather than a ✓ it has
            not earned or a ✕ it has not deserved. It is the line that says
            what the wait is for. */}
        <span
          aria-label={step.pending ? "in progress" : undefined}
          className={cx(
            styles.sysStepIcon,
            step.pending ? styles.pending : step.ok ? styles.ok : styles.fail,
          )}
        >
          {step.pending ? "" : step.ok ? "✓" : "×"}
        </span>
        <span className={styles.sysStepLabel}>{step.label}</span>
        {inline ? (
          step.url ? (
            <a
              className={styles.sysStepValue}
              href={step.url}
              rel="noreferrer"
              target="_blank"
            >
              {inline}
            </a>
          ) : (
            <span className={styles.sysStepValue}>{inline}</span>
          )
        ) : null}
        <span className={styles.sysGrow} />
        {block ? (
          <button
            aria-expanded={open}
            className={styles.sysStepMore}
            onClick={() => setOverride(!open)}
            type="button"
          >
            <Icon.ChevDown
              size={11}
              style={{
                transform: open ? "rotate(0)" : "rotate(-90deg)",
                transition: "transform .15s",
              }}
            />
            {step.output ? "output" : "error"}
          </button>
        ) : null}
        <span className={styles.sysStepDuration}>
          {step.pending ? "…" : step.duration}
        </span>
      </div>
      {/* Shown in full, wrapped and selectable — a bootstrap failure is usually
          a stack trace, and one clipped line of it is no use to anyone. */}
      {block && open ? (
        <pre className={styles.sysStepError}>{block}</pre>
      ) : null}
    </li>
  );
};
